import { useState } from "react";
import { Link } from "react-router-dom";
import { Phone, Play } from "lucide-react";
import StatusBadge from "@/components/StatusBadge";
import { isVideoUrl } from "@/components/ImageUpload";
import { LANDLORD } from "@/data/landlord";

export default function RoomCard({ room: r, propertyTitle, propertyId }) {
  const media = [r.image, ...(r.gallery || [])].filter(Boolean);
  const [active, setActive] = useState(0);
  const enquiry = `${LANDLORD.phoneHref}?text=${encodeURIComponent(
    `Hi Ricky Rooms Lettings, I'm interested in "${r.title}"${propertyTitle ? ` at ${propertyTitle}` : ""}. Is it still available?`
  )}`;

  const current = media[active];

  return (
    <div
      data-testid={`room-card-${r.id}`}
      className="rounded-2xl border border-[#E4DBD0] dark:border-[#352F2B] bg-white dark:bg-[#1A1715] overflow-hidden"
    >
      {media.length > 0 && (
        <div>
          {isVideoUrl(current) ? (
            <video
              key={current}
              src={current}
              controls
              playsInline
              data-testid={`room-card-image-${r.id}`}
              className="h-44 w-full object-cover bg-black"
            />
          ) : (
            <img
              src={current}
              alt={r.title}
              loading="lazy"
              data-testid={`room-card-image-${r.id}`}
              className="h-44 w-full object-cover"
            />
          )}
          {media.length > 1 && (
            <div className="flex gap-2 px-3 pt-3 overflow-x-auto no-scrollbar">
              {media.map((src, i) => (
                <button
                  key={i}
                  type="button"
                  data-testid={`room-card-thumb-${r.id}-${i}`}
                  onClick={() => setActive(i)}
                  className={`relative shrink-0 rounded-md overflow-hidden border-2 transition-colors ${
                    i === active ? "border-[#A05A32]" : "border-transparent opacity-70 hover:opacity-100"
                  }`}
                >
                  {isVideoUrl(src) ? (
                    <span className="relative block h-12 w-16 bg-black">
                      <video src={src} muted playsInline preload="metadata" className="h-12 w-16 object-cover" />
                      <Play className="absolute inset-0 m-auto h-4 w-4 text-white drop-shadow" fill="white" />
                    </span>
                  ) : (
                    <img src={src} alt={`${r.title} photo ${i + 1}`} className="h-12 w-16 object-cover" />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      <div className="p-5">
        <div className="flex items-start justify-between gap-3">
          <p className="font-medium text-[#1A1715] dark:text-[#F7F4F0]">{r.title}</p>
          <StatusBadge status={r.status} testId={`room-card-status-${r.id}`} />
        </div>
        <p className="mt-2 font-serif text-2xl text-[#A05A32] dark:text-[#D9773F]">
          £{Number(r.rent).toLocaleString()}
          <span className="text-sm font-sans text-[#8A837C] dark:text-[#8E867E]"> /mo</span>
          {r.size_m2 ? (
            <span className="ml-1.5 text-sm font-sans text-[#8A837C] dark:text-[#8E867E]">· {r.size_m2} m²</span>
          ) : null}
          {r.ensuite && (
            <span className="ml-2 align-middle inline-flex items-center rounded-full border border-[#B9DEC8] bg-[#E8F2EC] dark:bg-[#122A1E] dark:border-[#1B4D36] px-2.5 py-0.5 text-xs text-[#1E5235] dark:text-[#6EE7B7]">
              En-suite
            </span>
          )}
        </p>
        {r.description && (
          <p className="mt-2 text-sm text-[#57524E] dark:text-[#C7BEB5] leading-relaxed">{r.description}</p>
        )}
        <div className="mt-3 flex items-center gap-4 flex-wrap">
          <a
            href={enquiry}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`room-card-call-${r.id}`}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-[#A05A32] dark:text-[#D9773F] hover:underline"
          >
            <Phone className="h-3.5 w-3.5" /> WhatsApp us about this room
          </a>
          {propertyId && (
            <Link
              to={`/property/${propertyId}`}
              data-testid={`room-card-view-house-${r.id}`}
              className="inline-flex items-center gap-1.5 text-sm text-[#57524E] dark:text-[#C7BEB5] hover:text-[#A05A32] dark:hover:text-[#D9773F] transition-colors"
            >
              View house →
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
